import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import UFOSegment from "@atlaskit/react-ufo/segment";

import { ColumnLayout } from "@/libraries/design/foundations";
import {
    CustomerTestimonials,
    TestimonialProps
} from "@/libraries/design/patterns/customer-testimonials/src/customer-testimonials";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";

export const CarouselSection = () => {
    return (
        <UFOSegment name="customers-page-testimonial">
            <ColumnLayout layout="8-4">
                <h1 css={h1Styles}>
                    <FormattedMessage
                        id="CustomersLandingPage.CustomerStoriesCarouselSection.header"
                        values={{
                            span: (chunks: ReactNode) => {
                                return <span>{chunks}</span>;
                            }
                        }}
                        defaultMessage="Customer stories <span>driven by the power of teamwork</span>"
                    />
                </h1>
            </ColumnLayout>
            <CustomerTestimonials
                testimonials={Testimonials()}
                productKey="customers"
            />
        </UFOSegment>
    );
};

const h1Styles = css({
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontWeight: 400
    }
});

const Testimonials = () => {
    const intl = useIntl();

    const mercedesBenzTestimonial: TestimonialProps = {
        name: "Magnus Östberg",
        logo: {
            contentType: "image",
            src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12T9B.svg",
            alt: "Mercedes Benz"
        },
        title: intl.formatMessage({
            id: "CustomersLandingPage.testimonial.mercedes-benz.title",
            defaultMessage: "Chief Software Officer, Mercedes-Benz"
        }),
        image: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12N9T8.png",
        quote: intl.formatMessage({
            id: "CustomersLandingPage.testimonial.mercedes-benz.quote",
            defaultMessage:
                "We can only achieve our goals for speed and deliver on our promise for software-defined vehicles when we have a great platform. That’s why we rely on Atlassian."
        }),
        link: {
            text: intl.formatMessage({
                id: "CustomersLandingPage.testimonial.link.text",
                defaultMessage: "Read customer story"
            }),
            url: useLocalizedHref("/customers/mercedes-benz")
        },
        label: "Mercedes Benz"
    };

    const ciscoTestimonial: TestimonialProps = {
        name: "Jason Andrews",
        logo: {
            contentType: "image",
            src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EVAP.svg",
            alt: "Cisco"
        },
        title: intl.formatMessage({
            id: "CustomersLandingPage.testimonial.cisco.title",
            defaultMessage:
                "Vice President of Strategy & Planning – Engineering Operations, Cisco"
        }),
        image: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EQOG.png",
        quote: intl.formatMessage({
            id: "CustomersLandingPage.testimonial.cisco.quote",
            defaultMessage:
                "As we’ve started to build a System of Work with Atlassian, tooling spend is down 54% annually, and we’ve been able to reinvest 40% of our OpEx expenses back into the business within three years – without dropping work."
        }),
        link: {
            text: intl.formatMessage({
                id: "CustomersLandingPage.testimonial.link.text",
                defaultMessage: "Read customer story"
            }),
            url: useLocalizedHref("/customers/cisco")
        },
        label: "Cisco"
    };

    const dominosTestimonial: TestimonialProps = {
        name: "Matthias Hansen",
        logo: {
            contentType: "image",
            src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12LMY1.svg",
            alt: "Domino's"
        },
        title: intl.formatMessage({
            id: "CustomersLandingPage.testimonial.dominos.title",
            defaultMessage: "Group CTO, Domino's"
        }),
        image: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12E20H.png",
        quote: intl.formatMessage({
            id: "CustomersLandingPage.testimonial.dominos.quote",
            defaultMessage:
                "At Domino’s, we have a motto: ‘Every order counts.’ Rolling out the Atlassian system of work allowed us to successfully deliver on that promise."
        }),
        link: {
            text: intl.formatMessage({
                id: "CustomersLandingPage.testimonial.link.text",
                defaultMessage: "Read customer story"
            }),
            url: useLocalizedHref("/customers/dominos")
        },
        label: "Domino's"
    };

    const deliveryHeroTestimonial: TestimonialProps = {
        name: "Jousef Waggad",
        logo: {
            contentType: "image",
            src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EEB7.png",
            alt: "Delivery Hero"
        },
        title: intl.formatMessage({
            id: "CustomersLandingPage.testimonial.delivery-hero.title",
            defaultMessage: "Atlassian Administrator, Delivery Hero"
        }),
        image: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EEB1.png",
        quote: intl.formatMessage({
            id: "CustomersLandingPage.testimonial.delivery-hero.quote",
            defaultMessage:
                "After comparing collaboration and productivity tools, I knew Atlassian should be our #1 platform. Everything else felt too simple or too complex. Atlassian was intuitive enough for our basic users and advanced enough for our power users."
        }),
        link: {
            text: intl.formatMessage({
                id: "CustomersLandingPage.testimonial.link.text",
                defaultMessage: "Read customer story"
            }),
            url: useLocalizedHref("/customers/delivery-hero")
        },
        label: "Delivery Hero"
    };

    const brevilleTestimonial = {
        name: "Roni Nestorovski",
        logo: {
            contentType: "image",
            src: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT14QNK.png",
            alt: "Breville"
        },
        title: intl.formatMessage({
            id: "CustomersLandingPage.testimonial.breville.title",
            defaultMessage: "Global Service Delivery Manager, Breville"
        }),
        image: "https://dam-cdn.atl.orangelogic.com/CDNLink/AT17M86.png",
        quote: intl.formatMessage({
            id: "CustomersLandingPage.testimonial.breville.quote",
            defaultMessage:
                "In no time, we had a single point of contact for all support across Breville."
        }),
        link: {
            text: intl.formatMessage({
                id: "CustomersLandingPage.testimonial.link.text",
                defaultMessage: "Read customer story"
            }),
            url: useLocalizedHref("/customers/breville")
        },
        label: "Breville"
    };

    return [
        mercedesBenzTestimonial,
        ciscoTestimonial,
        dominosTestimonial,
        deliveryHeroTestimonial,
        brevilleTestimonial
    ];
};
